/**
 * MEVIA v4.0 - Catalogs Management Panel
 * Admin UI for managing company catalogs
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  BookOpen, Plus, Edit2, Trash2, RefreshCw, Search,
  FileText, Users, Briefcase, MapPin, GraduationCap,
  Heart, UserCheck, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const CATALOG_TYPES = [
  { key: 'tipos_contrato', label: { es: 'Tipos de Contrato', en: 'Contract Types' }, icon: FileText },
  { key: 'modalidades_trabajo', label: { es: 'Modalidades de Trabajo', en: 'Work Modalities' }, icon: Briefcase },
  { key: 'jornadas_laborales', label: { es: 'Jornadas Laborales', en: 'Work Schedules' }, icon: Users },
  { key: 'tipos_documento', label: { es: 'Tipos de Documento', en: 'Document Types' }, icon: FileText },
  { key: 'causas_baja', label: { es: 'Causas de Baja', en: 'Termination Reasons' }, icon: UserCheck },
  { key: 'estados_civiles', label: { es: 'Estados Civiles', en: 'Marital Status' }, icon: Heart },
  { key: 'parentescos', label: { es: 'Parentescos', en: 'Relationships' }, icon: Users },
  { key: 'generos', label: { es: 'Géneros', en: 'Genders' }, icon: Users },
  { key: 'niveles_educacion', label: { es: 'Niveles de Educación', en: 'Education Levels' }, icon: GraduationCap },
];

const TEXTS = {
  es: {
    title: 'Catálogos',
    subtitle: 'Gestiona los catálogos maestros de tu empresa',
    selectCatalog: 'Selecciona un catálogo',
    search: 'Buscar...',
    add: 'Agregar',
    edit: 'Editar',
    delete: 'Eliminar',
    save: 'Guardar',
    cancel: 'Cancelar',
    refresh: 'Actualizar',
    code: 'Código',
    nameEs: 'Nombre (ES)',
    nameEn: 'Nombre (EN)',
    description: 'Descripción',
    order: 'Orden',
    active: 'Activo',
    global: 'Global',
    company: 'Empresa',
    newItem: 'Nuevo Item',
    editItem: 'Editar Item',
    confirmDelete: '¿Eliminar este item?',
    deleteWarning: 'Esta acción desactivará el item del catálogo.',
    noItems: 'No hay items en este catálogo',
    itemCreated: 'Item creado exitosamente',
    itemUpdated: 'Item actualizado',
    itemDeleted: 'Item eliminado',
    error: 'Error al procesar la solicitud',
    globalCatalogs: 'Catálogos Globales',
    globalDesc: 'Items predefinidos disponibles para todas las empresas',
    companyCatalogs: 'Catálogos de Empresa',
    companyDesc: 'Items personalizados de tu empresa',
    geo: 'Geografía',
    countries: 'Países',
    states: 'Estados/Provincias',
  },
  en: {
    title: 'Catalogs',
    subtitle: 'Manage your company master catalogs',
    selectCatalog: 'Select a catalog',
    search: 'Search...',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    refresh: 'Refresh',
    code: 'Code',
    nameEs: 'Name (ES)',
    nameEn: 'Name (EN)',
    description: 'Description',
    order: 'Order',
    active: 'Active',
    global: 'Global',
    company: 'Company',
    newItem: 'New Item',
    editItem: 'Edit Item',
    confirmDelete: 'Delete this item?',
    deleteWarning: 'This action will deactivate the catalog item.',
    noItems: 'No items in this catalog',
    itemCreated: 'Item created successfully',
    itemUpdated: 'Item updated',
    itemDeleted: 'Item deleted',
    error: 'Error processing request',
    globalCatalogs: 'Global Catalogs',
    globalDesc: 'Predefined items available to all companies',
    companyCatalogs: 'Company Catalogs',
    companyDesc: 'Custom items for your company',
    geo: 'Geography',
    countries: 'Countries',
    states: 'States/Provinces',
  }
};

const CatalogsManagement = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const texts = TEXTS[language] || TEXTS.es;

  const [selectedCatalog, setSelectedCatalog] = useState('tipos_contrato');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    codigo: '',
    nombre_es: '',
    nombre_en: '',
    descripcion_es: '',
    descripcion_en: '',
    orden: 0
  });

  // Geo states
  const [geoView, setGeoView] = useState(null); // 'countries' | 'states'
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('MX');

  useEffect(() => {
    if (selectedCatalog && !geoView) {
      loadCatalogItems();
    }
  }, [selectedCatalog]);

  useEffect(() => {
    if (geoView === 'countries') {
      loadCountries();
    } else if (geoView === 'states') {
      loadStates(selectedCountry);
    }
  }, [geoView, selectedCountry]);

  const loadCatalogItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/catalogs/${selectedCatalog}?include_inactive=true`);
      if (response.data.success) {
        setItems(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error loading catalog:', error);
      toast.error(texts.error);
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/catalogs/geo/paises');
      if (response.data.success) {
        setCountries(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error loading countries:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStates = async (countryCode) => {
    setLoading(true);
    try {
      const response = await axios.get(`/catalogs/geo/estados/${countryCode}`);
      if (response.data.success) {
        setStates(response.data.data || []);
      }
    } catch (error) {
      logger.error('Error loading states:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        codigo: item.codigo,
        nombre_es: item.nombre_es,
        nombre_en: item.nombre_en || '',
        descripcion_es: item.descripcion || '',
        descripcion_en: '',
        orden: item.orden || 0
      });
    } else {
      setEditingItem(null);
      setFormData({
        codigo: '',
        nombre_es: '',
        nombre_en: '',
        descripcion_es: '',
        descripcion_en: '',
        orden: items.length + 1
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.codigo || !formData.nombre_es) {
      toast.error(language === 'es' ? 'Código y nombre son requeridos' : 'Code and name are required');
      return;
    }

    try {
      if (editingItem && !editingItem.es_global) {
        await axios.put(`/catalogs/${selectedCatalog}/${editingItem.id}`, formData);
        toast.success(texts.itemUpdated);
      } else {
        await axios.post(`/catalogs/${selectedCatalog}?empresa_id=${user.empresa_id}`, formData);
        toast.success(texts.itemCreated);
      }
      setDialogOpen(false);
      loadCatalogItems();
    } catch (error) {
      logger.error('Error saving item:', error);
      toast.error(error.response?.data?.detail || texts.error);
    }
  };

  const handleDelete = async () => {
    if (!editingItem || editingItem.es_global) return;
    
    try {
      await axios.delete(`/catalogs/${selectedCatalog}/${editingItem.id}`);
      toast.success(texts.itemDeleted);
      setDeleteDialogOpen(false);
      setEditingItem(null);
      loadCatalogItems();
    } catch (error) {
      logger.error('Error deleting item:', error);
      toast.error(error.response?.data?.detail || texts.error);
    }
  };

  const filteredItems = items.filter(item => 
    item.nombre_es?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.codigo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const globalItems = filteredItems.filter(item => item.es_global);
  const companyItems = filteredItems.filter(item => !item.es_global);

  const selectedCatalogInfo = CATALOG_TYPES.find(c => c.key === selectedCatalog);
  const IconComponent = selectedCatalogInfo?.icon || BookOpen;

  return (
    <div className="space-y-6">
      {/* Catalog Selector */}
      <div className="flex flex-wrap gap-2">
        {CATALOG_TYPES.map((catalog) => {
          const Icon = catalog.icon;
          return (
            <Button
              key={catalog.key}
              variant={selectedCatalog === catalog.key && !geoView ? "default" : "outline"}
              size="sm"
              onClick={() => { setSelectedCatalog(catalog.key); setGeoView(null); }}
              className="gap-2"
            >
              <Icon className="h-4 w-4" />
              {catalog.label[language]}
            </Button>
          );
        })}
        <div className="border-l mx-2" />
        <Button
          variant={geoView === 'countries' ? "default" : "outline"}
          size="sm"
          onClick={() => setGeoView('countries')}
          className="gap-2"
        >
          <Globe className="h-4 w-4" />
          {texts.countries}
        </Button>
        <Button
          variant={geoView === 'states' ? "default" : "outline"}
          size="sm"
          onClick={() => setGeoView('states')}
          className="gap-2"
        >
          <MapPin className="h-4 w-4" />
          {texts.states}
        </Button>
      </div>

      {/* Main Content */}
      {geoView ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {geoView === 'countries' ? <Globe className="h-5 w-5" /> : <MapPin className="h-5 w-5" />}
                  {geoView === 'countries' ? texts.countries : texts.states}
                </CardTitle>
                <CardDescription>
                  {geoView === 'countries' 
                    ? (language === 'es' ? 'Países disponibles en el sistema' : 'Countries available in the system')
                    : (language === 'es' ? 'Estados/Provincias por país' : 'States/Provinces by country')}
                </CardDescription>
              </div>
              {geoView === 'states' && (
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(c => (
                      <SelectItem key={c.codigo} value={c.codigo}>
                        {c.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{texts.code}</TableHead>
                  <TableHead>{texts.nameEs}</TableHead>
                  <TableHead>{texts.nameEn}</TableHead>
                  {geoView === 'countries' && <TableHead>{language === 'es' ? 'Código Tel.' : 'Phone Code'}</TableHead>}
                  <TableHead>{texts.active}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(geoView === 'countries' ? countries : states).map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono">{item.codigo}</TableCell>
                    <TableCell>{item.nombre_es || item.nombre}</TableCell>
                    <TableCell>{item.nombre_en || '-'}</TableCell>
                    {geoView === 'countries' && <TableCell>{item.codigo_telefono}</TableCell>}
                    <TableCell>
                      <Badge variant={item.activo ? "success" : "secondary"}>
                        {item.activo ? texts.active : 'Inactivo'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={texts.search}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={loadCatalogItems}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {texts.refresh}
              </Button>
              <Button size="sm" onClick={() => handleOpenDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                {texts.add}
              </Button>
            </div>
          </div>

          {/* Global Catalogs */}
          {globalItems.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="h-4 w-4 text-blue-500" />
                  {texts.globalCatalogs}
                </CardTitle>
                <CardDescription>{texts.globalDesc}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">{texts.code}</TableHead>
                      <TableHead>{texts.nameEs}</TableHead>
                      <TableHead>{texts.nameEn}</TableHead>
                      <TableHead className="w-20">{texts.order}</TableHead>
                      <TableHead className="w-24">{texts.active}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {globalItems.map((item) => (
                      <TableRow key={item.id} className="bg-blue-50/30">
                        <TableCell className="font-mono text-sm">{item.codigo}</TableCell>
                        <TableCell>{item.nombre_es}</TableCell>
                        <TableCell className="text-gray-500">{item.nombre_en || '-'}</TableCell>
                        <TableCell>{item.orden}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                            {texts.global}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Company Catalogs */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <IconComponent className="h-4 w-4 text-teal-600" />
                {texts.companyCatalogs}
              </CardTitle>
              <CardDescription>{texts.companyDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              {companyItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>{texts.noItems}</p>
                  <Button variant="outline" size="sm" className="mt-4" onClick={() => handleOpenDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    {texts.add}
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-24">{texts.code}</TableHead>
                      <TableHead>{texts.nameEs}</TableHead>
                      <TableHead>{texts.nameEn}</TableHead>
                      <TableHead className="w-20">{texts.order}</TableHead>
                      <TableHead className="w-24">{texts.active}</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companyItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-sm">{item.codigo}</TableCell>
                        <TableCell>{item.nombre_es}</TableCell>
                        <TableCell className="text-gray-500">{item.nombre_en || '-'}</TableCell>
                        <TableCell>{item.orden}</TableCell>
                        <TableCell>
                          <Badge variant={item.activo ? "success" : "secondary"}>
                            {item.activo ? texts.active : 'Inactivo'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => { setEditingItem(item); setDeleteDialogOpen(true); }}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? texts.editItem : texts.newItem}</DialogTitle>
            <DialogDescription>
              {selectedCatalogInfo?.label[language]}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">{texts.code} *</label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                  placeholder="CODE"
                  maxLength={20}
                  disabled={editingItem?.es_global}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">{texts.order}</label>
                <Input
                  type="number"
                  value={formData.orden}
                  onChange={(e) => setFormData({ ...formData, orden: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{texts.nameEs} *</label>
              <Input
                value={formData.nombre_es}
                onChange={(e) => setFormData({ ...formData, nombre_es: e.target.value })}
                placeholder={language === 'es' ? 'Nombre en español' : 'Spanish name'}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{texts.nameEn}</label>
              <Input
                value={formData.nombre_en}
                onChange={(e) => setFormData({ ...formData, nombre_en: e.target.value })}
                placeholder={language === 'es' ? 'Nombre en inglés' : 'English name'}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">{texts.description}</label>
              <Input
                value={formData.descripcion_es}
                onChange={(e) => setFormData({ ...formData, descripcion_es: e.target.value })}
                placeholder={language === 'es' ? 'Descripción opcional' : 'Optional description'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {texts.cancel}
            </Button>
            <Button onClick={handleSave} disabled={editingItem?.es_global}>
              {texts.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{texts.confirmDelete}</DialogTitle>
            <DialogDescription>{texts.deleteWarning}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {texts.cancel}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {texts.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CatalogsManagement;
